import { Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, Calendar, Shield, BookOpen, Trophy, Medal, Target, ArrowLeft, Swords, Film, Crosshair, Server, MessageSquare, GripVertical, Ghost, Search, Box, Radar, Bell, X, Monitor, Crown, Zap } from 'lucide-react';
import { DEFAULT_AVATAR } from '../lib/constants';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import '../pages/AdminDashboardPage.css';
import './MainLayout.css';
import { useNavigationGuard } from '../context/NavigationGuardContext';
import { TeamSetupWizard } from '../components/TeamSetupWizard';
import { useSmartPolling } from '../hooks/useSmartPolling';

/** Sidebar nav items that can be reordered via drag-and-drop */
interface NavItem {
    id: string;
    path: string;
    icon: React.ElementType;
    labelKey: string;
}

const DEFAULT_NAV_ITEMS: NavItem[] = [
    { id: 'calendar', path: 'calendar', icon: Calendar, labelKey: 'sidebar.calendar' },
    { id: 'practice', path: 'practice', icon: Target, labelKey: 'sidebar.practice' },
    { id: 'stratbook', path: 'stratbook', icon: BookOpen, labelKey: 'sidebar.stratbook' },
    { id: 'league', path: 'leagues', icon: Trophy, labelKey: 'sidebar.league' },
    { id: 'matches', path: 'matches', icon: Swords, labelKey: 'sidebar.matches' },
    { id: 'server', path: 'game-server', icon: Server, labelKey: 'sidebar.gameServer' },
    { id: 'demoTool2d', path: 'demo-tool', icon: Film, labelKey: 'sidebar.demoTool2d' },
    { id: 'scout', path: 'scout', icon: Search, labelKey: 'sidebar.scout' },
    { id: 'pros', path: 'pros', icon: Medal, labelKey: 'sidebar.pros' },
    { id: 'stats', path: 'roster-display', icon: Users, labelKey: 'sidebar.stats' },
    { id: 'pracSearch', path: 'prac-search', icon: Radar, labelKey: 'sidebar.pracSearch' },
];

function parseStoredOrder(sidebarOrder: string | null | undefined): string[] | null {
    if (!sidebarOrder) return null;
    try {
        return JSON.parse(sidebarOrder);
    } catch { /* ignore */ }
    return null;
}

function orderNavItems(items: NavItem[], orderIds: string[] | null): NavItem[] {
    if (!orderIds) return items;
    const itemMap = new Map(items.map(i => [i.id, i]));
    const ordered: NavItem[] = [];
    for (const id of orderIds) {
        const item = itemMap.get(id);
        if (item) {
            ordered.push(item);
            itemMap.delete(id);
        }
    }
    // Insert Pros below Analyze for existing saved orders; preserve deliberate reorders.
    const pros = itemMap.get('pros');
    if (pros) {
        const scoutIndex = ordered.findIndex(item => item.id === 'scout');
        if (scoutIndex >= 0) {
            ordered.splice(scoutIndex + 1, 0, pros);
            itemMap.delete('pros');
        }
    }
    itemMap.forEach(item => ordered.push(item));
    return ordered;
}

import { useGhostChangeTracker } from '../context/GhostChangeTrackerContext';
import { CheckCircle, XCircle, Trash2, ClipboardList, Save, Loader2, Check, Square, CheckSquare, ChevronUp } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/** Ghost-mode banner with deferred change tracker */
function GhostBanner({ teamId, refreshUser, navigate }: { teamId: string | undefined; refreshUser: () => Promise<void>; navigate: (path: string, opts?: any) => void }) {
    const { pending, toggleCheck, setAllChecked, applyChecked, discardAll, isApplying } = useGhostChangeTracker();
    const [showPanel, setShowPanel] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const floatingButtonRef = useRef<HTMLButtonElement>(null);
    const collapseButtonRef = useRef<HTMLButtonElement>(null);
    const restoreToggleFocus = useRef(false);

    const toggleCollapsed = (value: boolean) => {
        restoreToggleFocus.current = true;
        setCollapsed(value);
    };
    useEffect(() => {
        if (!restoreToggleFocus.current) return;
        (collapsed ? floatingButtonRef : collapseButtonRef).current?.focus();
        restoreToggleFocus.current = false;
    }, [collapsed]);

    const handleLeave = async () => {
        try {
            if (pending.some(p => p.applyStatus === 'pending')) {
                if (!window.confirm('You have unapplied changes. Leave without applying?')) return;
            }
            await axios.post(`${API}/admin/teams/${teamId}/leave-ghost`);
            discardAll();
            await refreshUser();
            navigate('/admin', { replace: true });
        } catch (err) {
            console.error('Failed to leave ghost mode', err);
        }
    };

    const handleApply = async () => {
        await applyChecked();
        await refreshUser();
    };

    const handleDiscard = () => {
        if (pending.length > 0 && !window.confirm('Discard all pending changes? This cannot be undone.')) return;
        discardAll();
        setShowPanel(false);
    };

    const pendingCount = pending.filter(p => p.applyStatus === 'pending').length;
    const appliedCount = pending.filter(p => p.applyStatus === 'applied').length;
    const errorCount = pending.filter(p => p.applyStatus === 'error').length;
    const reviewCount = pendingCount + errorCount;
    const reviewDescription = [pendingCount ? `${pendingCount} pending changes` : '', errorCount ? `${errorCount} failed changes` : ''].filter(Boolean).join(', ');
    const checkedCount = pending.filter(p => p.checked && p.applyStatus === 'pending').length;
    const allChecked = pending.length > 0 && pending.every(p => p.checked || p.applyStatus === 'applied');

    if (collapsed) return (
        <button
            ref={floatingButtonRef}
            type="button"
            className="admin-ghost-floating-button"
            aria-label={`Expand Ghost Mode${reviewDescription ? `, ${reviewDescription}` : ''}`}
            aria-expanded={false}
            title={`Show Ghost Mode${reviewDescription ? ` · ${reviewDescription}` : ''}`}
            onClick={() => toggleCollapsed(false)}
        >
            <Ghost size={16} aria-hidden="true" />
            {reviewCount > 0 && <span className={`admin-ghost-floating-count${errorCount ? ' has-errors' : ''}`} aria-hidden="true">{reviewCount > 99 ? '99+' : reviewCount}</span>}
        </button>
    );

    return (
        <>
            <div className="admin-ghost-banner">
                <Ghost size={16} />
                <span className="admin-ghost-message">Ghost Mode — You are invisible in this team</span>
                {pending.length > 0 && (
                    <button
                        className={`ghost-changes-btn ${showPanel ? 'active' : ''}`}
                        onClick={() => setShowPanel(!showPanel)}
                    >
                        <ClipboardList size={13} />
                        Review Changes
                        <span className="ghost-change-count">{pendingCount}</span>
                    </button>
                )}
                <button onClick={handleLeave}>Back to Admin</button>
                <button
                    ref={collapseButtonRef}
                    type="button"
                    className="admin-ghost-collapse-button"
                    aria-label="Collapse Ghost Mode"
                    aria-expanded={true}
                    title="Collapse Ghost Mode"
                    onClick={() => toggleCollapsed(true)}
                ><ChevronUp size={17} aria-hidden="true" /></button>
            </div>
            {showPanel && pending.length > 0 && (
                <div className="ghost-changes-panel">
                    <div className="ghost-changes-header">
                        <div className="ghost-changes-stats">
                            {pendingCount > 0 && <span className="ghost-stat pending">{pendingCount} pending</span>}
                            {appliedCount > 0 && <span className="ghost-stat success"><CheckCircle size={12} /> {appliedCount} applied</span>}
                            {errorCount > 0 && <span className="ghost-stat error"><XCircle size={12} /> {errorCount} failed</span>}
                        </div>
                        <div className="ghost-changes-actions">
                            <button
                                className="ghost-select-all-btn"
                                onClick={() => setAllChecked(!allChecked)}
                                title={allChecked ? 'Deselect all' : 'Select all'}
                            >
                                {allChecked ? <CheckSquare size={14} /> : <Square size={14} />}
                            </button>
                            <button
                                className="ghost-apply-btn"
                                onClick={handleApply}
                                disabled={isApplying || checkedCount === 0}
                            >
                                {isApplying ? <Loader2 size={13} className="ghost-spinner" /> : <Save size={13} />}
                                {isApplying ? 'Applying...' : `Apply Selected (${checkedCount})`}
                            </button>
                            <button
                                className="ghost-discard-btn"
                                onClick={handleDiscard}
                                disabled={isApplying}
                            >
                                <Trash2 size={12} /> Discard All
                            </button>
                        </div>
                    </div>
                    <div className="ghost-changes-list">
                        {[...pending].reverse().map(c => (
                            <div
                                key={c.id}
                                className={`ghost-change-item ${c.applyStatus || 'pending'}`}
                                onClick={() => { if (c.applyStatus === 'pending') toggleCheck(c.id); }}
                                style={{ cursor: c.applyStatus === 'pending' ? 'pointer' : 'default' }}
                            >
                                <span className="ghost-change-checkbox">
                                    {c.applyStatus === 'applied' ? (
                                        <CheckCircle size={16} className="ghost-icon-applied" />
                                    ) : c.applyStatus === 'applying' ? (
                                        <Loader2 size={16} className="ghost-spinner" />
                                    ) : c.applyStatus === 'error' ? (
                                        <XCircle size={16} className="ghost-icon-error" />
                                    ) : c.checked ? (
                                        <Check size={16} className="ghost-icon-checked" />
                                    ) : (
                                        <Square size={16} className="ghost-icon-unchecked" />
                                    )}
                                </span>
                                <span className="ghost-change-time">
                                    {c.timestamp.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </span>
                                <span className="ghost-change-method">{c.method}</span>
                                <span className="ghost-change-label">{c.label}</span>
                                {c.applyStatus === 'error' && c.errorMessage && (
                                    <span className="ghost-change-error">{c.errorMessage}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

export function MainLayout() {
    const { user, refreshUser, token } = useAuth();
    const { t } = useTranslation();
    const { teamId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [tunnelTransition, setTunnelTransition] = useState(false);

    const currentMembership = (user as any)?.teamMemberships?.find((m: any) => m.team.id === teamId);
    const showSetupWizard = currentMembership && 
                            currentMembership.team.ownerId === (user as any)?.id && 
                            currentMembership.team.setupCompleted === false;

    // Premium Upsell modal — shown once after wizard completion
    const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
    const [upsellExiting, setUpsellExiting] = useState(false);
    useEffect(() => {
        if (localStorage.getItem('showPremiumUpsell') === 'true') {
            localStorage.removeItem('showPremiumUpsell');
            // Small delay to let the dashboard render first
            const t = setTimeout(() => setShowPremiumUpsell(true), 800);
            return () => clearTimeout(t);
        }
    }, []);
    const dismissUpsell = () => {
        setUpsellExiting(true);
        setTimeout(() => { setShowPremiumUpsell(false); setUpsellExiting(false); }, 300);
    };
    const goToPremium = () => {
        setUpsellExiting(true);
        setTimeout(() => { setShowPremiumUpsell(false); setUpsellExiting(false); navigate('/premium?tab=team'); }, 300);
    };

    // Kick detection remains immediate on route/focus changes, while the
    // periodic safety check pauses in hidden tabs and never overlaps.
    const checkMembership = useCallback(async (signal: AbortSignal) => {
        if (!token || !teamId || user?.isAdmin) return;
        try {
            const res = await axios.get(`${API}/users/me`, {
                headers: { Authorization: `Bearer ${token}` },
                signal,
                timeout: 8000,
            });
            if (signal.aborted) return;
            const freshUser = res.data;
            const memberships: { team: { id: string }; leftReason?: string | null }[] = freshUser.teamMemberships || [];
            const isStillMember = memberships.some(
                m => m.team.id === teamId && m.leftReason !== 'PENDING_INVITE',
            );
            if (!isStillMember) {
                await refreshUser();
                if (!signal.aborted) navigate('/', { replace: true });
            }
        } catch (error) {
            if (!signal.aborted) throw error;
        }
    }, [navigate, refreshUser, teamId, token, user?.isAdmin]);

    useSmartPolling(checkMembership, {
        enabled: !!token && !!teamId && !user?.isAdmin,
        intervalMs: 60_000,
        jitterRatio: 0.1,
        restartKey: location.pathname,
    });

    // Draggable nav items — load from user's saved order
    const [navItems, setNavItems] = useState<NavItem[]>(() =>
        orderNavItems(DEFAULT_NAV_ITEMS, parseStoredOrder((user as any)?.sidebarOrder))
    );

    // Re-sync when user data loads/changes
    useEffect(() => {
        if ((user as any)?.sidebarOrder) {
            setNavItems(orderNavItems(DEFAULT_NAV_ITEMS, parseStoredOrder((user as any).sidebarOrder)));
        }
    }, [(user as any)?.sidebarOrder]);
    const [dragItemId, setDragItemId] = useState<string | null>(null);
    const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
    const dragNodeRef = useRef<HTMLDivElement | null>(null);
    const [dragTooltip, setDragTooltip] = useState<{ x: number; y: number } | null>(null);

    const handleDragStart = useCallback((e: React.DragEvent, itemId: string) => {
        setDragItemId(itemId);
        dragNodeRef.current = e.currentTarget as HTMLDivElement;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', itemId);
        // Slight delay to let the browser snapshot the drag image before we change opacity
        requestAnimationFrame(() => {
            if (dragNodeRef.current) dragNodeRef.current.style.opacity = '0.4';
        });
    }, []);

    const handleDragEnd = useCallback(() => {
        if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1';
        setDragItemId(null);
        setDropTargetIndex(null);
        dragNodeRef.current = null;
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTargetIndex(index);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        if (dragItemId === null) return;

        setNavItems(prev => {
            const dragIndex = prev.findIndex(i => i.id === dragItemId);
            if (dragIndex === -1 || dragIndex === dropIndex) return prev;

            const newItems = [...prev];
            const [removed] = newItems.splice(dragIndex, 1);
            newItems.splice(dropIndex, 0, removed);

            // Persist to server
            const orderIds = newItems.map(i => i.id);
            axios.patch(`${API}/users/me`, { sidebarOrder: JSON.stringify(orderIds) }).catch(() => { });
            return newItems;
        });

        setDragItemId(null);
        setDropTargetIndex(null);
        if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1';
        dragNodeRef.current = null;
    }, [dragItemId]);

    const [explicitSidebarHidden, setSidebarHidden] = useState(() => {
        return sessionStorage.getItem('tunnelOverlayActive') === 'true';
    });
    // Direct Finder loads can mount before the focus-event listener is attached.
    const sidebarHidden = explicitSidebarHidden || (location.pathname.endsWith('/pros')
        && new URLSearchParams(location.search).get('view') === 'finder');
    const [isTunnelMode, setIsTunnelMode] = useState(() => {
        return sessionStorage.getItem('tunnelOverlayActive') === 'true';
    });

    useEffect(() => {
        sessionStorage.removeItem('tunnelOverlayActive');
    }, []);

    useEffect(() => {
        if (sidebarHidden && isTunnelMode) {
            const handleReveal = () => {
                setSidebarHidden(false);
                setIsTunnelMode(false);
            };
            window.addEventListener('dashboardFadeReady', handleReveal);
            
            const timer = setTimeout(handleReveal, 2500);
            
            return () => {
                window.removeEventListener('dashboardFadeReady', handleReveal);
                clearTimeout(timer);
            };
        }
    }, [sidebarHidden, isTunnelMode]);

    // Explicit Sidebar Control (Focus Mode overrides)
    useEffect(() => {
        const handleExplicitHide = () => setSidebarHidden(true);
        const handleExplicitShow = () => setSidebarHidden(false);
        
        window.addEventListener('hideSidebar', handleExplicitHide);
        window.addEventListener('showSidebar', handleExplicitShow);
        
        return () => {
            window.removeEventListener('hideSidebar', handleExplicitHide);
            window.removeEventListener('showSidebar', handleExplicitShow);
        };
    }, []);


    const { checkNavigation } = useNavigationGuard();

    // ── Notifications ──
    const [showNotifications, setShowNotifications] = useState(false);
    const notifBtnRef = useRef<HTMLButtonElement>(null);
    const notifPanelRef = useRef<HTMLDivElement>(null);

    interface Notification {
        id: string;
        type: string;
        title: string;
        body: string;
        createdAt: string;
        read: boolean;
        link?: string;
    }

    const [notifications, setNotifications] = useState<Notification[]>([]);

    const fetchNotifications = useCallback(async (signal: AbortSignal) => {
        if (!token) return;
        const res = await axios.get(`${import.meta.env.VITE_API_URL || `${API}`}/notifications`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { scope: 'team', teamId },
            signal,
            timeout: 8000,
        });
        setNotifications(res.data);
    }, [token, teamId]);

    useSmartPolling(fetchNotifications, {
        enabled: !!token && !!teamId,
        intervalMs: 60_000,
        jitterRatio: 0.1,
        maxBackoffMs: 240_000,
        restartKey: teamId,
    });

    const unreadCount = notifications.filter(n => !n.read).length;

    const markAllRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        axios.patch(`${import.meta.env.VITE_API_URL || `${API}`}/notifications/read-all`, {}, {
            headers: { Authorization: `Bearer ${token}` },
            params: { scope: 'team', teamId },
        }).catch(() => { });
    };

    const handleNotifClick = (notif: Notification) => {
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        axios.patch(`${import.meta.env.VITE_API_URL || `${API}`}/notifications/${notif.id}/read`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        }).catch(() => { });
        if (notif.link) {
            navigate(`/team/${teamId}/${notif.link}`);
        }
        setShowNotifications(false);
    };

    const timeAgo = (iso: string) => {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    // Close notification overlay on outside click
    useEffect(() => {
        if (!showNotifications) return;
        const handleClick = (e: MouseEvent) => {
            if (
                notifPanelRef.current && !notifPanelRef.current.contains(e.target as Node) &&
                notifBtnRef.current && !notifBtnRef.current.contains(e.target as Node)
            ) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showNotifications]);

    const notifTypeIcon = (type: string) => {
        switch (type) {
            case 'SCRIM_CHAT': return '💬';
            case 'SCRIM_REQUEST': return '🤝';
            case 'OFFER_ACCEPTED': return '✅';
            case 'PRACTICE_CREATED': return '⚔️';
            case 'PRACTICE_REMINDER': return '⏰';
            case 'MATCH_REMINDER': return '🎮';
            case 'MEMBER_JOIN': return '➕';
            case 'MEMBER_LEAVE': return '➖';
            case 'CALENDAR_GAP': return '📅';
            case 'DEMO_MISSING': return '📁';
            case 'SUBSCRIPTION_CANCELLED': return '💳';
            default: return '🔔';
        }
    };



    const navLinkClass = (isActive: boolean) =>
        `team-nav-link flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group relative cursor-pointer ${isActive
            ? 'bg-blue-600/15 text-blue-400 border-l-4 border-blue-500 pl-3 shadow-inner'
            : 'text-slate-400 hover:bg-white/5 hover:text-white border-l-4 border-transparent hover:shadow-inner'
        }`;

    const isNavActive = (path: string) => {
        const full = `/team/${teamId}/${path}`;
        let isActive = location.pathname === full || location.pathname.startsWith(full + '/');
        
        // Treat lineups and callouts as part of stratbook navigation item since they are merged visually
        if (path === 'stratbook' && !isActive) {
            const lineupsFull = `/team/${teamId}/lineups`;
            const calloutsFull = `/team/${teamId}/callouts`;
            const stratsyncFull = `/team/${teamId}/stratsync-lineups`;
            isActive = location.pathname === lineupsFull || location.pathname.startsWith(lineupsFull + '/') ||
                       location.pathname === calloutsFull || location.pathname.startsWith(calloutsFull + '/') ||
                       location.pathname === stratsyncFull || location.pathname.startsWith(stratsyncFull + '/');
        }

        // If editing an opponent strategy, override highlight to Practice instead of Stratbook
        if (location.pathname.includes('/stratbook/') && location.search.includes('opponent=1')) {
            if (path === 'stratbook') return false;
            if (path === 'practice') return true;
        }
        
        return isActive;
    };
    const isNavExact = (path: string) => location.pathname === `/team/${teamId}/${path}` || location.pathname === `/team/${teamId}/${path}/`;
    const teamSpacePage = location.pathname.split('/').filter(Boolean)[2] || 'unknown';

    return (
        <div className="team-space-shell min-h-screen bg-black">
            {/* Tunnel-Vision Dark Overlay — fades to completely black */}
            <div
                className="fixed inset-0 z-[200] pointer-events-none"
                style={{
                    opacity: tunnelTransition ? 1 : 0,
                    transition: 'opacity 1.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: '#000',
                }}
            />
            <div className={`flex h-screen ${sidebarHidden ? 'bg-black' : 'bg-background'} text-foreground`}
                style={{
                    transform: tunnelTransition ? 'scale(0.4)' : 'scale(1)',
                    filter: tunnelTransition ? 'blur(12px) brightness(0.2)' : 'none',
                    transition: 'transform 1.4s cubic-bezier(0.4, 0, 0.2, 1), filter 1.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >

                {/* Sidebar */}
                <aside className={`team-sidebar w-48 bg-slate-950/60 backdrop-blur-xl border-r border-slate-700/30 flex flex-col relative z-20 shadow-[4px_0_24px_rgba(0,0,0,0.5)] ${location.pathname.includes('/second-screen') ? 'hidden' : ''}`} style={{
                    opacity: (sidebarHidden || location.pathname.includes('/dryrun') || location.pathname.match(/\/stratbook\/[a-zA-Z0-9_-]+/)) ? 0 : 1,
                    transform: (sidebarHidden || location.pathname.includes('/dryrun') || location.pathname.match(/\/stratbook\/[a-zA-Z0-9_-]+/)) ? 'translateX(-20px)' : 'translateX(0)',
                    width: (sidebarHidden || location.pathname.includes('/dryrun') || location.pathname.match(/\/stratbook\/[a-zA-Z0-9_-]+/)) ? 0 : '12rem',
                    overflow: 'hidden',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>

                    <nav className="team-sidebar-nav flex-1 px-4 flex flex-col justify-center">
                        {/* Fixed items: Dashboard, Team & Communications */}
                        <div className="team-sidebar-fixed pb-4 mb-4 border-b border-slate-700/30">
                            {/* In-Game Mode Navigation Link */}
                            <div 
                                onClick={() => checkNavigation(() => navigate(`/team/${teamId}/second-screen`))} 
                                className={`team-nav-link flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group relative cursor-pointer team-sidebar-ingame mb-3 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)] ${isNavExact('second-screen') ? 'border-l-4 !border-l-cyan-400 pl-3' : 'border-l-4 !border-l-transparent'}`}
                            >
                                <Monitor size={20} className="text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.5)]" />
                                <span className="font-bold tracking-wide uppercase text-xs">In-Game Mode</span>
                            </div>

                            <div onClick={() => checkNavigation(() => navigate(`/team/${teamId}/dashboard`))} className={navLinkClass(isNavExact('dashboard'))}>
                                <LayoutDashboard size={20} />
                                <span>{t('sidebar.dashboard')}</span>
                            </div>
                            <div onClick={() => checkNavigation(() => navigate(`/team/${teamId}/roster`))} className={navLinkClass(isNavActive('roster'))}>
                                <Shield size={20} />
                                <span>{t('sidebar.team')}</span>
                            </div>
                            <div onClick={() => checkNavigation(() => navigate(`/team/${teamId}/communications`))} className={navLinkClass(isNavActive('communications'))}>
                                <MessageSquare size={20} />
                                <span>{t('sidebar.communications')}</span>
                            </div>
                        </div>

                        {/* Draggable nav items */}
                        <div className="space-y-0">
                            {navItems.map((item, index) => {
                                const Icon = item.icon;
                                const isBeingDragged = dragItemId === item.id;
                                const isDropTarget = dropTargetIndex === index && dragItemId !== null && dragItemId !== item.id;

                                return (
                                    <div
                                        key={item.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, item.id)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDrop={(e) => handleDrop(e, index)}
                                        className="relative group"
                                        style={{ opacity: isBeingDragged ? 0.4 : 1 }}
                                    >
                                        {/* Drop indicator line */}
                                        {isDropTarget && (
                                            <div className="absolute -top-[1px] left-2 right-2 h-[2px] bg-blue-500 rounded-full z-20 shadow-[0_0_6px_rgba(59,130,246,0.5)]" />
                                        )}
                                        <div
                                            onClick={() => checkNavigation(() => navigate(`/team/${teamId}/${item.path}`))}
                                            className={navLinkClass(isNavActive(item.path))}
                                        >
                                            <Icon size={20} />
                                            <span>{t(item.labelKey)}</span>
                                        </div>
                                        <GripVertical
                                            size={14}
                                            className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                                            onMouseEnter={(e) => setDragTooltip({ x: e.clientX, y: e.clientY })}
                                            onMouseMove={(e) => setDragTooltip({ x: e.clientX, y: e.clientY })}
                                            onMouseLeave={() => setDragTooltip(null)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </nav>

                    {/* Fixed bottom section: Profile, Back + Notifications */}
                    <div className="team-sidebar-footer p-4 border-t border-slate-700/30 space-y-1">

                        <div
                            onClick={() => checkNavigation(() => navigate(`/team/${teamId}/profile`))}
                            className={navLinkClass(location.pathname === `/team/${teamId}/profile` || location.pathname === `/team/${teamId}/profile/${user?.id}`)}
                        >
                            <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 group-hover:border-blue-500/50 flex-shrink-0 overflow-hidden transition-colors shadow-inner">
                                <img
                                    src={user?.avatarUrl || DEFAULT_AVATAR}
                                    alt="Me"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                                    }}
                                />
                            </div>
                            <span className="truncate">{user?.ingameName || user?.username || 'My Profile'}</span>
                        </div>

                        <div className="flex items-center gap-1 relative">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    checkNavigation(() => {
                                        setTunnelTransition(true);
                                        sessionStorage.setItem('menuFadeIn', 'true');
                                        sessionStorage.setItem('tunnelOverlayActive', 'true');
                                        setTimeout(() => navigate('/'), 1500);
                                    });
                                }}
                                /* Glowing so the way out of the team space stands out from
                                   the nav links above it. */
                                className="flex items-center gap-3 px-4 py-3 flex-1 rounded-lg border border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 hover:border-blue-400/60 hover:text-white transition-all duration-200 shadow-[0_0_18px_rgba(59,130,246,0.18)] hover:shadow-[0_0_24px_rgba(59,130,246,0.32)] font-bold"
                            >
                                <ArrowLeft size={20} />
                                <span>{t('sidebar.backToMenu')}</span>
                            </button>

                            <button
                                ref={notifBtnRef}
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="relative p-2.5 rounded-lg text-slate-400 hover:bg-white/5 hover:shadow-inner hover:text-white transition-all duration-200"
                                title="Notifications"
                            >
                                <Bell size={18} />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-lg">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </aside>

                {/* ── Notification Overlay Panel ── */}
                {showNotifications && (
                    <div
                        ref={notifPanelRef}
                        className="fixed z-50"
                        style={{
                            bottom: 60,
                            left: 56,
                            width: 340,
                        }}
                    >
                        <div style={{
                            background: '#1e293b',
                            border: '1px solid rgba(51, 65, 85, 0.6)',
                            borderRadius: 14,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                            overflow: 'hidden',
                            maxHeight: 440,
                            display: 'flex',
                            flexDirection: 'column' as const,
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(51, 65, 85, 0.4)',
                            }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>
                                    Notifications {unreadCount > 0 && <span style={{ color: '#60a5fa', fontSize: 11 }}>({unreadCount} new)</span>}
                                </span>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {unreadCount > 0 && (
                                        <button
                                            onClick={markAllRead}
                                            style={{ fontSize: 10, color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                                        >
                                            Mark all read
                                        </button>
                                    )}
                                    {notifications.length > 0 && (
                                        <button
                                            onClick={() => {
                                                setNotifications([]);
                                                axios.delete(`${import.meta.env.VITE_API_URL || `${API}`}/notifications/all`, {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                    params: { scope: 'team', teamId },
                                                }).catch(() => { });
                                            }}
                                            style={{ fontSize: 10, color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                                        >
                                            Clear all
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowNotifications(false)}
                                        style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 2 }}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            </div>
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                {notifications.length === 0 ? (
                                    <div style={{ padding: 24, textAlign: 'center', color: 'rgba(148,163,184,0.5)', fontSize: 11 }}>
                                        No notifications
                                    </div>
                                ) : (
                                    notifications.map(notif => (
                                        <div
                                            key={notif.id}
                                            onClick={() => handleNotifClick(notif)}
                                            style={{
                                                display: 'flex',
                                                gap: 10,
                                                padding: '10px 16px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid rgba(51, 65, 85, 0.2)',
                                                background: notif.read ? 'transparent' : 'rgba(59, 130, 246, 0.04)',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(51, 65, 85, 0.3)')}
                                            onMouseLeave={e => (e.currentTarget.style.background = notif.read ? 'transparent' : 'rgba(59, 130, 246, 0.04)')}
                                        >
                                            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }}>{notifTypeIcon(notif.type)}</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: notif.read ? '#94a3b8' : (notif.type === 'SUBSCRIPTION_CANCELLED' ? '#c084fc' : '#f1f5f9') }}>
                                                        {notif.title}
                                                    </span>
                                                    {!notif.read && (
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: notif.type === 'SUBSCRIPTION_CANCELLED' ? '#c084fc' : '#3b82f6', flexShrink: 0 }} />
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 10, color: 'rgba(148, 163, 184, 0.6)', marginTop: 2, lineHeight: 1.4 }}>
                                                    {notif.body}
                                                </div>
                                                <div style={{ fontSize: 9, color: 'rgba(148, 163, 184, 0.4)', marginTop: 3 }}>
                                                    {timeAgo(notif.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <main className={`flex-1 bg-transparent relative flex flex-col ${(location.pathname.includes('/communications') || location.pathname.includes('/scout') || location.pathname.includes('/pros') || location.pathname.includes('/lineups') || location.pathname.includes('/demo-tool') || location.pathname.includes('/prac-search') || location.pathname.includes('/practice') || location.pathname.match(/\/stratbook\/[a-zA-Z0-9_-]+/)) ? 'overflow-hidden' : 'overflow-auto'
                    }`}>
                    {/* Global Background (Always visible) — hide during transition to prevent flash */}
                    {!sidebarHidden && <div className="team-space-bg-grid" aria-hidden="true" />}
                    {!sidebarHidden && <div className="team-space-bg-surface" aria-hidden="true" />}

                    {/* Admin Ghost-Mode Banner */}
                    {user?.isAdmin && (
                        <GhostBanner key={teamId} teamId={teamId} refreshUser={refreshUser} navigate={navigate} />
                    )}

                    <div
                        className={`team-space-content relative z-10 flex-1 flex flex-col min-h-0 ${location.pathname.includes('/communications') ? 'h-full' : location.pathname.match(/\/stratbook\/[a-zA-Z0-9_-]+/) ? 'h-full p-0' : (location.pathname.includes('/lineups') || location.pathname.includes('/demo-tool') || location.pathname.includes('/prac-search') || location.pathname.includes('/practice')) ? 'h-full p-4 sm:p-6 lg:p-8' : location.pathname.includes('/pros') ? 'h-full p-4 sm:p-6' : location.pathname.includes('/scout') ? 'h-full px-4 sm:px-8 pb-0 pt-0' : 'p-8 flex-none'
                            }`}
                        data-team-space-page={teamSpacePage}
                    >
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* Drag tooltip */}
            {dragTooltip && (
                <div
                    className="fixed z-[9999] pointer-events-none px-3 py-1.5 rounded-lg bg-slate-900/95 border border-slate-700 shadow-xl text-xs font-bold text-slate-300 whitespace-nowrap backdrop-blur-sm"
                    style={{ left: dragTooltip.x + 14, top: dragTooltip.y - 10 }}
                >
                    Drag to reorder
                </div>
            )}

            {showSetupWizard && teamId && (
                <TeamSetupWizard 
                    teamId={teamId}
                    onComplete={() => {
                        refreshUser();
                    }}
                />
            )}

            {/* Premium Upsell Modal — shown once after Welcome Wizard completes */}
            {showPremiumUpsell && (
                <div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${upsellExiting ? 'opacity-0' : 'opacity-100'}`}>
                    <div className={`w-[520px] bg-slate-900/95 border border-slate-700/50 rounded-2xl shadow-[0_0_100px_rgba(0,0,0,0.9)] overflow-hidden transition-all duration-300 ${upsellExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
                        {/* Header with shimmer */}
                        <div className="relative p-8 text-center overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-purple-500/10" />
                            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-500/30 shadow-[0_0_30px_rgba(245,158,11,0.15)]">
                                    <Crown size={28} className="text-amber-400" />
                                </div>
                                <h2 className="text-2xl font-bold text-white mb-2">Team Setup Complete! 🎉</h2>
                                <p className="text-sm text-slate-400 max-w-sm mx-auto">Your team hub is ready. Unlock the full potential with Premium — advanced analytics, unlimited storage, and more.</p>
                            </div>
                        </div>

                        {/* Two options */}
                        <div className="px-8 pb-6 grid grid-cols-2 gap-3">
                            {/* Free Plan */}
                            <button
                                onClick={dismissUpsell}
                                className="group flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-slate-700/40 bg-slate-800/30 hover:border-slate-600 hover:bg-slate-800/50 transition-all duration-200"
                            >
                                <div className="w-10 h-10 rounded-full bg-slate-700/50 flex items-center justify-center border border-slate-600/50 group-hover:border-slate-500">
                                    <Zap size={18} className="text-slate-400" />
                                </div>
                                <div className="text-center">
                                    <div className="font-bold text-white text-sm">Free Plan</div>
                                    <div className="text-[10px] text-slate-500 mt-1">Basic features to get started</div>
                                </div>
                                <span className="text-[10px] text-slate-600 font-bold uppercase tracking-widest">Continue Free</span>
                            </button>

                            {/* Premium Plan */}
                            <button
                                onClick={goToPremium}
                                className="group relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-amber-500/30 bg-gradient-to-b from-amber-500/5 to-amber-600/10 hover:border-amber-500/50 hover:from-amber-500/10 hover:to-amber-600/15 transition-all duration-200 shadow-[0_0_20px_rgba(245,158,11,0.05)]"
                            >
                                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-[8px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-lg">Recommended</div>
                                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/30 group-hover:border-amber-400/50 shadow-[0_0_15px_rgba(245,158,11,0.1)]">
                                    <Crown size={18} className="text-amber-400" />
                                </div>
                                <div className="text-center">
                                    <div className="font-bold text-amber-300 text-sm">Get Premium</div>
                                    <div className="text-[10px] text-amber-500/60 mt-1">Full access to all features</div>
                                </div>
                                <span className="text-[10px] text-amber-400 font-bold uppercase tracking-widest">Upgrade Now →</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
